import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import Navbar from "../components/Navbar";
import { useAuth } from "../context/AuthContext";
import { api, formatApiError } from "../lib/api";

/** Public view of a matrimonial profile. Contact hidden until owner approves. */
export default function ProfileView() {
  const { id } = useParams();
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [error, setError] = useState("");
  const [interest, setInterest] = useState(null);
  const [isOwner, setIsOwner] = useState(false);
  const [sending, setSending] = useState(false);
  const [message, setMessage] = useState("");
  const [showComposer, setShowComposer] = useState(false);

  useEffect(() => {
    if (loading) return;
    if (!user || typeof user !== "object") {
      navigate("/login", { replace: true, state: { from: `/profile/${id}` } });
    }
  }, [user, loading, navigate, id]);

  const loadAll = async () => {
    try {
      const [prof, rel] = await Promise.all([
        api.get(`/profile/${id}`),
        api.get(`/interests/with/${id}`),
      ]);
      setProfile(prof.data);
      setInterest(rel.data.interest);
      setIsOwner(Boolean(rel.data.is_owner));
    } catch (e) {
      setError(formatApiError(e));
    }
  };

  useEffect(() => {
    if (!user || typeof user !== "object") return;
    loadAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, user]);

  const sendInterest = async () => {
    setSending(true);
    try {
      await api.post("/interests", { to_profile_id: id, message });
      toast.success("Interest sent");
      setShowComposer(false);
      setMessage("");
      loadAll();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSending(false);
    }
  };

  if (error) {
    return (
      <div>
        <Navbar />
        <div className="container py-5">
          <div className="sb-card">
            <h2 className="h5 mb-2">Can't load this profile</h2>
            <p className="text-muted mb-3">{error}</p>
            <Link to="/search" className="btn btn-outline-primary">Back to search</Link>
          </div>
        </div>
      </div>
    );
  }

  if (!profile) {
    return (
      <div>
        <Navbar />
        <div className="container py-5"><div className="sb-card">Loading…</div></div>
      </div>
    );
  }

  const p = profile.personal || {};
  const f = profile.family || {};
  const ec = profile.education_career || {};
  const l = profile.lifestyle || {};
  const c = profile.contact || {};
  const photos = profile.photos || [];
  const heroPhoto = profile.photo_url || photos[0] || "";
  const contactHidden = !c.phone && !c.email;

  return (
    <div>
      <Navbar />
      <div className="container py-4" data-testid="profile-view">
        <div className="row g-4">
          <div className="col-lg-4">
            <div className="sb-card p-0 overflow-hidden">
              <div className="sb-profile-hero">
                {heroPhoto ? (
                  <img src={heroPhoto} alt={p.full_name} />
                ) : (
                  <div className="sb-profile-hero-empty">
                    {(p.full_name || "?").charAt(0)}
                  </div>
                )}
              </div>
              <div className="p-3">
                <div className="sb-kicker mb-1">Profile</div>
                <h1 className="h4 mb-1">{p.full_name || "Member"}</h1>
                <div className="small text-muted">
                  {[p.gender && p.gender[0].toUpperCase() + p.gender.slice(1), p.height, p.marital_status]
                    .filter(Boolean)
                    .join(" · ")}
                </div>
              </div>
            </div>

            {photos.length > 0 && (
              <div className="sb-card mt-3">
                <div className="sb-kicker mb-2">Gallery</div>
                <div className="sb-gallery">
                  {photos.map((ph, i) => (
                    <div key={i} className="sb-gallery-item">
                      <img src={ph} alt="" />
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          <div className="col-lg-8">
            {l.about_me && (
              <div className="sb-card mb-3">
                <div className="sb-kicker mb-1">About</div>
                <p className="mb-0" style={{ whiteSpace: "pre-line" }}>{l.about_me}</p>
              </div>
            )}

            <div className="row g-3">
              <Section title="Personal" rows={[
                ["Date of Birth", p.date_of_birth],
                ["Religion", p.religion],
                ["Caste", p.caste],
                ["Sub-caste", p.sub_caste],
                ["Gothra", p.gothra],
                ["Mother Tongue", p.mother_tongue],
                ["Manglik", p.manglik],
                ["Complexion", p.complexion],
                ["Blood Group", p.blood_group],
              ]} />
              <Section title="Family" rows={[
                ["Father", f.father_name && `${f.father_name}${f.father_occupation ? " · " + f.father_occupation : ""}`],
                ["Mother", f.mother_name && `${f.mother_name}${f.mother_occupation ? " · " + f.mother_occupation : ""}`],
                ["Siblings", f.siblings],
                ["Family Type", f.family_type],
                ["Family Values", f.family_values],
                ["Native Place", f.native_place],
              ]} />
              <Section title="Education & Career" rows={[
                ["Education", ec.highest_qualification],
                ["Field", ec.field_of_study],
                ["College", ec.college],
                ["Occupation", ec.occupation],
                ["Company", ec.company],
                ["Income", ec.annual_income],
                ["Work Location", ec.work_location],
              ]} />
              <Section title="Lifestyle" rows={[
                ["Diet", l.diet],
                ["Drinking", l.drinking],
                ["Smoking", l.smoking],
                ["Hobbies", l.hobbies],
                ["Languages", l.languages_known],
              ]} />
            </div>

            <div className="sb-card mt-3">
              <div className="sb-kicker mb-1">Contact</div>
              {contactHidden ? (
                <ContactHidden
                  interest={interest}
                  isOwner={isOwner}
                  onExpress={() => setShowComposer(true)}
                />
              ) : (
                <div className="row g-2 small">
                  {c.phone && <div className="col-md-6"><strong>Phone:</strong> {c.phone}</div>}
                  {c.email && <div className="col-md-6"><strong>Email:</strong> {c.email}</div>}
                  {c.city && <div className="col-md-6"><strong>City:</strong> {c.city}</div>}
                  {c.state && <div className="col-md-6"><strong>State:</strong> {c.state}</div>}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {showComposer && (
        <div
          className="position-fixed top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center"
          style={{ background: "rgba(26,22,20,0.55)", zIndex: 1050 }}
          onClick={() => setShowComposer(false)}
          data-testid="interest-composer"
        >
          <div className="sb-card mx-3" style={{ maxWidth: 480 }} onClick={(e) => e.stopPropagation()}>
            <div className="sb-kicker mb-2">Express interest</div>
            <h2 className="h4 mb-1">Send a short note to {profile?.personal?.full_name || "this member"}</h2>
            <p className="text-muted small">Contact details unlock only after they accept.</p>
            <textarea
              rows={3}
              className="form-control"
              maxLength={280}
              placeholder="Hello! I loved your profile — would like to know more…"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              data-testid="interest-message"
            />
            <div className="d-flex justify-content-end gap-2 mt-3">
              <button
                className="btn btn-outline-primary"
                onClick={() => setShowComposer(false)}
                data-testid="interest-cancel"
              >
                Cancel
              </button>
              <button
                className="btn btn-primary"
                onClick={sendInterest}
                disabled={sending}
                data-testid="interest-send"
              >
                {sending ? "Sending…" : "Send interest"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function ContactHidden({ interest, isOwner, onExpress }) {
  if (isOwner) {
    return <div className="text-muted">This is you — your contact is always visible to yourself.</div>;
  }
  if (!interest) {
    return (
      <div className="d-flex flex-wrap justify-content-between align-items-center gap-2">
        <div className="text-muted">Contact is hidden. Express interest to unlock — they'll be notified.</div>
        <button className="btn btn-primary btn-sm" onClick={onExpress} data-testid="express-interest">
          Express interest
        </button>
      </div>
    );
  }
  if (interest.status === "pending") {
    const label = interest.from_user_id === interest.to_user_id
      ? "Pending"
      : "Interest sent — awaiting their response";
    return (
      <div className="d-flex flex-wrap justify-content-between align-items-center gap-2">
        <div className="text-muted">{label}</div>
        <span className="sb-badge-soft" data-testid="interest-pending">Pending</span>
      </div>
    );
  }
  if (interest.status === "declined") {
    return <div className="text-muted" data-testid="interest-declined">This request was declined. Contact remains private.</div>;
  }
  return null;
}

function Section({ title, rows }) {
  const filled = rows.filter(([, v]) => v);
  if (filled.length === 0) return null;
  return (
    <div className="col-12">
      <div className="sb-card">
        <div className="sb-kicker mb-2">{title}</div>
        <div className="row g-2">
          {filled.map(([k, v]) => (
            <div className="col-md-6 small" key={k}>
              <span className="text-muted" style={{ minWidth: 130, display: "inline-block" }}>{k}</span>
              <strong>{v}</strong>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
